import { gql } from '@graphql/__generated__';

export const GET_VISIBILITY_TEAM_MEMBERS = gql(/* GraphQL */ `
  query GetVisibilityTeamMembers(
    $teamId: uuid!
    $twoYearsAgo: timestamptz!
    $teamReviewsWhere: reviews_bool_exp!
  ) {
    team_reviews: reviews_aggregate(where: $teamReviewsWhere) {
      aggregate {
        count
        avg {
          rating
        }
      }
    }
    teams_by_pk(id: $teamId) {
      id
      name
      teams_users(order_by: { order_nr: asc }) {
        user {
          id
          first_name
          last_name
          full_name
          display_name
          description
          active_since
          show_in_agency_pages
          languages
          services
          certifications
          facebook_profile
          instagram_profile
          tiktok_profile
          x_profile
          youtube_profile
          linked_in_profile
          phone_numbers(order_by: { primary: desc }, limit: 1) {
            id
            number
            primary
          }
          user_images(where: { is_primary: { _eq: true } }, limit: 1) {
            id
            is_primary
            image {
              id
              url
            }
          }
          reviews_aggregate(where: { completed: { _eq: true } }) {
            aggregate {
              count
              avg {
                rating
              }
            }
          }
          property_transactions_aggregate(
            where: {
              _not: { lot: { offer_type: { _eq: "rent" } } }
              is_published: { _eq: true }
            }
          ) {
            aggregate {
              count
            }
          }
          last_2_years_property_transactions_aggregate: property_transactions_aggregate(
            where: {
              signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
              _not: { lot: { offer_type: { _eq: "rent" } } }
              is_published: { _eq: true }
            }
          ) {
            aggregate {
              count
            }
          }
        }
      }
    }
  }
`);

export const GET_AGENT_TEAM_SETTINGS = gql(/* GraphQL */ `
  query GetAgentTeamSettings($userId: uuid!, $teamId: uuid!) {
    users_by_pk(id: $userId) {
      id
      first_name
      last_name
      show_in_agency_pages
      default_team_id
      teams_users(where: { team_id: { _eq: $teamId } }, limit: 1) {
        is_leader
        title
        deactivated
      }
    }

    teams(where: { teams_users: { user_id: { _eq: $userId } } }) {
      id
      name
    }
  }
`);

// Member-detail projection used by the profile-completion scorer
// (`profileCompletionScore.ts`). Kept distinct from `GetAgentTeamSettings`
// because the scorer needs the review/transaction aggregates below.
export const GET_TEAM_MEMBER_DETAILS = gql(/* GraphQL */ `
  query GetTeamMemberDetails(
    $userId: uuid!
    $teamId: uuid!
    $twoYearsAgo: timestamptz!
  ) {
    users_by_pk(id: $userId) {
      id
      first_name
      last_name
      full_name
      agent_slug
      show_in_agency_pages
      description
      active_since
      languages
      services
      emails(order_by: { primary: desc }, limit: 1) {
        id
        email
        primary
      }
      phone_numbers(order_by: { primary: desc }, limit: 1) {
        id
        number
        primary
      }
      default_team_id
      user_images(where: { is_primary: { _eq: true } }, limit: 1) {
        id
        image {
          id
          url
        }
      }
      teams_users(where: { team_id: { _eq: $teamId } }, limit: 1) {
        is_leader
        title
        deactivated
        team {
          id
          name
          cover_photo {
            id
            url
          }
        }
      }

      all_reviews_count: reviews_aggregate(
        where: { completed: { _eq: true } }
      ) {
        aggregate {
          count
          avg {
            rating
          }
        }
      }

      verified_reviews_count: reviews_aggregate(
        where: { completed: { _eq: true }, transaction_id: { _is_null: false } }
      ) {
        aggregate {
          count
        }
      }

      latest_review: reviews(
        where: { completed: { _eq: true } }
        order_by: { completed_at: desc }
        limit: 1
      ) {
        completed_at
      }
    }

    last_transaction: property_transactions(
      where: {
        seller_broker_id: { _eq: $userId }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        signature_date: { _is_null: false, _lte: "now()" }
        is_published: { _eq: true }
      }
      order_by: { signature_date: desc }
      limit: 1
    ) {
      signature_date
    }

    all_transactions_count: property_transactions_aggregate(
      where: {
        seller_broker_id: { _eq: $userId }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        is_published: { _eq: true }
      }
    ) {
      aggregate {
        count
      }
    }
    last_2_years_transactions: property_transactions(
      where: {
        is_published: { _eq: true }
        seller_broker_id: { _eq: $userId }
        _not: { lot: { offer_type: { _eq: "rent" } } }
        signature_date: { _gte: $twoYearsAgo, _lte: "now()" }
      }
      order_by: { purchase_price: asc_nulls_last }
    ) {
      purchase_price
    }

    teams(where: { teams_users: { user_id: { _eq: $userId } } }) {
      id
      name
    }
  }
`);

export const CREATE_TEAM_INVITATION = gql(/* GraphQL */ `
  mutation CreateTeamInvitation($invitation: invitations_insert_input!) {
    insert_invitations_one(object: $invitation) {
      id
    }
  }
`);

export const CHECK_EXISTING_TEAM_MEMBER = gql(/* GraphQL */ `
  query CheckExistingTeamMember(
    $email: citext!
    $teamId: uuid!
    $tenantId: uuid!
  ) {
    users(
      where: {
        emails: { email: { _eq: $email } }
        teams_users: {
          team_id: { _eq: $teamId }
          deactivated: { _eq: false }
          team: { tenant_id: { _eq: $tenantId } }
        }
      }
      limit: 1
    ) {
      id
      first_name
      last_name
    }
  }
`);

export const CHECK_EXISTING_INVITATION = gql(/* GraphQL */ `
  query CheckExistingInvitation(
    $email: citext!
    $teamId: uuid!
    $tenantId: uuid!
  ) {
    invitations_aggregate(
      where: {
        email: { _eq: $email }
        team_id: { _eq: $teamId }
        tenant_id: { _eq: $tenantId }
        accepted_at: { _is_null: true }
        cancelled_at: { _is_null: true }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`);

export const CANCEL_EXISTING_INVITATIONS = gql(/* GraphQL */ `
  mutation CancelExistingInvitations(
    $email: citext!
    $teamId: uuid!
    $tenantId: uuid!
  ) {
    update_invitations(
      where: {
        email: { _eq: $email }
        team_id: { _eq: $teamId }
        tenant_id: { _eq: $tenantId }
        accepted_at: { _is_null: true }
        cancelled_at: { _is_null: true }
      }
      _set: { cancelled_at: "now()" }
    ) {
      affected_rows
    }
  }
`);

export const PUBLISH_USER = gql(/* GraphQL */ `
  mutation PublishUser($userId: uuid!, $agentSlug: String!) {
    update_users_by_pk(
      pk_columns: { id: $userId }
      _set: { show_in_agency_pages: true, agent_slug: $agentSlug }
    ) {
      id
      show_in_agency_pages
      agent_slug
    }
  }
`);

export const UNPUBLISH_USER = gql(/* GraphQL */ `
  mutation UnpublishUser($userId: uuid!) {
    update_users_by_pk(
      pk_columns: { id: $userId }
      _set: { show_in_agency_pages: false }
    ) {
      id
      show_in_agency_pages
    }
  }
`);

export const UPDATE_TEAM_MEMBER_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateTeamMemberSettings(
    $userId: uuid!
    $teamId: uuid!
    $defaultTeamId: uuid
    $isLeader: Boolean!
    $title: String
    $deactivated: Boolean!
  ) {
    update_users_by_pk(
      pk_columns: { id: $userId }
      _set: { default_team_id: $defaultTeamId }
    ) {
      id
    }

    update_teams_users(
      where: { user_id: { _eq: $userId }, team_id: { _eq: $teamId } }
      _set: { is_leader: $isLeader, title: $title, deactivated: $deactivated }
    ) {
      affected_rows
    }
  }
`);
